import type { Dictionary, InsightsMessages } from '../models'

import { OriginsStepEnum } from '../components/insights/origins/origins.component'

import { KeywordsModeEnum, LocaleEnum, OriginIdEnum, QueriesTypeEnum } from '../enums'

const DEFAULT_ORIGINS_MESSAGES_EN = {
  [OriginIdEnum.SEARCH_BOX]: {
    description: 'Any query typed in the search box',
  },
  [OriginIdEnum.POPULAR_SEARCH]: {
    description: 'Query selected from the popular searches',
  },
  [OriginIdEnum.HISTORY_QUERY]: {
    description: 'Query selected from the search history',
  },
  [OriginIdEnum.QUERY_SUGGESTION]: {
    description: 'Query selected from the suggestions',
  },
  [OriginIdEnum.QUERY_SUGGESTION_WITH_FACET]: {
    description: 'Query filtered by category selected from the suggestions',
  },
  [OriginIdEnum.IDENTIFIER_SUGGESTION]: {
    description: 'Query selected from an ID suggestion',
  },
  [OriginIdEnum.PARTIAL_RESULT]: {
    description: 'Query generated from a partial suggestion',
  },
  [OriginIdEnum.SPELLCHECK]: {
    description: 'Query generated from a spell check suggestion',
  },
  [OriginIdEnum.RELATED_TAG]: {
    description: 'Query generated from a related tag',
  },
  [OriginIdEnum.NEXT_QUERY]: {
    description: 'Query generated from a next query',
  },
  [OriginIdEnum.SEMANTICS]: {
    description: 'Query generated from an AI-powered semantic suggestion',
  },
  [OriginIdEnum.URL]: {
    description: 'Query selected from a provided URL',
  },
}
const DEFAULT_KPIS_MESSAGES_EN = {
  queries: {
    valueLabel: 'queries',
  },
  noResults: {
    valueLabel: 'no results',
  },
  clicks: {
    valueLabel: 'clicks',
    percentageLabel: 'CTR',
  },
  findability: {
    valueLabel: 'success clicks',
    percentageLabel: 'findability',
  },
  add2cart: {
    valueLabel: 'add2carts',
    percentageLabel: 'Add2cart',
  },
  positionAvg: {
    valueLabel: 'avg. position',
  },
  checkout: {
    valueLabel: 'checkouts',
    percentageLabel: 'Checkout',
  },
  sessions: {
    valueLabel: 'sessions',
  },
  queriesPerSession: {
    valueLabel: 'Q/Session',
  },
  conversion: {
    valueLabel: 'Conversion',
  },
}

const INSIGHTS_MESSAGES_EN: InsightsMessages = {
  now: {
    totalQueries: 'Total queries',
    queries: {
      counter: 'Result queries',
      kpi: 'Queries',
    },
    clicks: 'Clicks',
    noResults: 'No results',
  },
  origins: {
    landing: {
      paragraph1:
        'The Search gives customers different ways to find what they are looking for: type a query or click a suggestion.',
      paragraph2: `The origin of this query could be <b>Organic</b>, <b>Predictive</b>, or <b>Refinement</b>.`,
    },
    steps: {
      [OriginsStepEnum.DEFAULT]: {
        label: 'Organic',
        description:
          'These queries are written without any kind of search suggestions. The customer types exactly what they are looking for.',
      },
      [OriginsStepEnum.PREDICTIVE]: {
        label: 'Predictive',
        description:
          "The customer doesn't type any query; they simply choose from relevant suggestions provided.",
      },
      [OriginsStepEnum.REFINEMENT]: {
        label: 'Refinement',
        description:
          'A combination of a query and some suggestions offered within the Search experience.',
      },
    },
    skipAnimation: 'Skip animation',
    component: {
      origins: {
        ...DEFAULT_ORIGINS_MESSAGES_EN,
        [OriginIdEnum.DEFAULT]: DEFAULT_ORIGINS_MESSAGES_EN[OriginIdEnum.SEARCH_BOX],
        [OriginIdEnum.DEFAULT_SEARCH]: DEFAULT_ORIGINS_MESSAGES_EN[OriginIdEnum.SEARCH_BOX],
        [OriginIdEnum.EMPATHIZE_TRENDING]: DEFAULT_ORIGINS_MESSAGES_EN[OriginIdEnum.POPULAR_SEARCH],
        [OriginIdEnum.TRENDING]: DEFAULT_ORIGINS_MESSAGES_EN[OriginIdEnum.POPULAR_SEARCH],
        [OriginIdEnum.HISTORY]: DEFAULT_ORIGINS_MESSAGES_EN[OriginIdEnum.HISTORY_QUERY],
        [OriginIdEnum.HISTORICAL]: DEFAULT_ORIGINS_MESSAGES_EN[OriginIdEnum.HISTORY_QUERY],
        [OriginIdEnum.EMPATHIZE]: DEFAULT_ORIGINS_MESSAGES_EN[OriginIdEnum.QUERY_SUGGESTION],
        [OriginIdEnum.AUTOCOMPLETE]: DEFAULT_ORIGINS_MESSAGES_EN[OriginIdEnum.QUERY_SUGGESTION],
        [OriginIdEnum.EMPATHIZE_CATEGORY]:
          DEFAULT_ORIGINS_MESSAGES_EN[OriginIdEnum.QUERY_SUGGESTION_WITH_FACET],
        [OriginIdEnum.PARTIALS]: DEFAULT_ORIGINS_MESSAGES_EN[OriginIdEnum.PARTIAL_RESULT],
        [OriginIdEnum.RELATED_TAGS]: DEFAULT_ORIGINS_MESSAGES_EN[OriginIdEnum.RELATED_TAG],
        [OriginIdEnum.RELATED]: DEFAULT_ORIGINS_MESSAGES_EN[OriginIdEnum.RELATED_TAG],
        [OriginIdEnum.EMPATHIZE_NEXT_QUERIES]: DEFAULT_ORIGINS_MESSAGES_EN[OriginIdEnum.NEXT_QUERY],
        [OriginIdEnum.WHATS_NEXT]: DEFAULT_ORIGINS_MESSAGES_EN[OriginIdEnum.NEXT_QUERY],
        [OriginIdEnum.WHATS_NEXT_CAROUSEL]: DEFAULT_ORIGINS_MESSAGES_EN[OriginIdEnum.NEXT_QUERY],
      },
    },
  },
  keywords: {
    main: {
      titles: {
        top: 'Top {count} terms by {mode}',
        compare: 'Comparative',
      },
      info: '<b>Growth:</b> Search terms trending now.<br> <b>Popularity:</b> Overall top search terms.',
      modes: {
        [KeywordsModeEnum.GROWTH]: 'Growth',
        [KeywordsModeEnum.POPULARITY]: 'Popularity',
      },
      noDataFound: 'No data found for this period and filters. Please, try other values.',
    },
    aside: {
      title: 'Top queries',
      noQueries: 'No queries found for this term',
    },
  },
  categories: {
    findabilityTitle: 'Performance by category',
    rootTitle: 'All categories',
    asideIdLabel: 'CAT_ID:',
    asideInfo:
      'Percentages may be over 100%, as multiple events can be triggered for a single query.',
    categoryLabel: 'Category',
    percentageLabel: 'Percentage',
    download: 'Download',
    tooltips: {
      queries:
        'Number of search requests performed for the category versus total queries for all categories',
      findability: 'Number of clicks on the first page of the SERP versus total queries',
      add2cart: 'Number of products added to the cart versus total queries',
      volume: 'Number of queries performed for the selected period',
      categoriesTable: {
        category: 'Name of the product category',
        percentage:
          'Number of queries expressed as a percentage of the total number for all categories',
        findability:
          'Number of clicks on the first page of the SERP versus total queries for the category',
        add2cart: 'Percentage of products added to cart for the category',
      },
      queriesTable: {
        query: 'Search query associated with the category selected',
        percentage:
          'Number of queries expressed as a percentage of the total number for the category',
        findability:
          'Number of clicks on the first page of the SERP versus total queries for the query',
        add2cart: 'Percentage of products added to cart for the query',
      },
    },
  },
  analytics: {
    kpis: Object.assign({}, DEFAULT_KPIS_MESSAGES_EN, {}),
    details: {
      overall: 'Showing overall data',
      selectedId: 'Showing data for "{terms}"',
    },
    kpisSuccess: {
      overall: 'Search success metrics',
      selectedId: 'Search success metrics for "{terms}"',
    },
    evolutionTitle: 'Evolution over time',
    distributionTitle: 'Queries distribution',
    previousPeriodLabel: 'Previous period {kpi}',
    currentPeriodLabel: 'Current period {kpi}',
    showPercentage: 'Show percentage',
    showRadarChart: 'Show chart',
    timezone: 'UTC time',
    volume: 'Volume',
    noFindability: 'no clicks',
    queryName: 'Query',
    product: 'Name',
    productId: 'ID',
    quantity: 'Quantity',
    queries: {
      queriesTypes: {
        [QueriesTypeEnum.ALL]: 'All queries',
        [QueriesTypeEnum.WITH_RESULTS]: 'Queries with results',
        [QueriesTypeEnum.WITHOUT_RESULTS]: 'Queries without results',
      },
      distributionTitle: 'Queries distribution for "{query}"',
      relatedTitle: 'Related products for "{query}"',
      search: {
        placeholder: 'Type a query',
        resultsSummary: '{count} queries with "{terms}"',
        noResultsFound: 'No results found for "{terms}"',
      },
      getBack: 'Get Back',
    },
  },
  toolReporting: {
    noResultsFound: {
      title: 'Oops!',
      description: {
        paragraph1: 'It seems this filtering has no results.',
        paragraph2: 'Check it out and try again!',
      },
    },
    preDatesSelected: {
      title: "Let's Start!",
      description: {
        paragraph1: "Select a date range and you'll see your",
        paragraph2: 'configurations right here!',
      },
    },
    columns: {
      name: 'Name',
      queries: 'Queries',
      ctr: 'CTR',
    },
    toggle: {
      graph: 'Graph',
      table: 'Table',
    },
    stats: {
      ctrAvg: 'CTR Average',
      totalClicks: 'Total clicks',
    },
    aside: {
      warningMessage: 'This promotion has been removed',
      sections: {
        created: {
          title: 'Created',
          creationDate: 'On',
          user: 'By',
        },
        ctr: 'CTR',
        schedule: 'Schedule',
        queries: 'Queries',
        promotion: 'Promotion',
        position: 'Position',
        averagePosition: {
          title: 'Average position',
          help: 'The position of a TOP can change because of the impact from algorithms, MZN or PZN',
        },
        evolution: 'Evolution over time',
      },
      dailyChart: {
        errorMessage: 'The requested data is not available or there is an error',
      },
    },
    ctrPerformance: {
      high: {
        label: 'High',
      },
      medium: {
        label: 'Medium',
      },
      low: {
        label: 'Low',
      },
    },
  },
  semantics: {
    header: {
      totalQueries: 'Total queries',
      clicksGained: 'Clicks gained',
      add2cartsGained: 'Add2carts gained',
      noResultsDrop: 'No results drop',
    },
    insightsBar: {
      title: {
        noResults: 'No results',
        interactionsGained: 'Interactions gained',
      },
      noResultsBar: {
        main: 'Before semantics',
        secondary: 'After semantics',
      },
      interactionsGainedBar: {
        secondary: {
          clicks: 'clicks',
          add2cart: 'a2c',
        },
        tooltip:
          'Percentages of the increase in interactions of clicks, add2carts, and checkouts when leveraging Semantics',
      },
    },
    semanticsQueriesTitle: '{type} queries',
    timelineGraph: {
      semanticsQueries: 'Semantics queries',
      noResults: 'No results',
      clicks: 'Clicks',
      add2carts: 'Add2carts',
    },
    table: {
      quantity: 'Quantity',
      clicks: 'Clicks',
      add2carts: 'Add2carts',
      checkouts: 'Checkouts',
    },
    downloadFile: 'SemanticsData',
  },
  relatedPrompts: {
    summary:
      'During the selected date range, the products displayed within the related prompts received <b>{clicks} Clicks</b> and <b>{add2carts} Add2carts</b>, out of a total of <b>{queries} Queries</b>.',
    tabs: {
      overall: 'Related Prompts overall',
      noResults: 'Related Prompts no-results',
      lowResults: 'Related Prompts low-results',
    },
    table: {
      relatedPrompts: 'Related Prompts',
      clicks: 'Clicks',
      add2carts: 'Add2carts',
      promptsClicks: 'Prompt clicks',
    },
    timelineGraph: {
      clicks: 'Clicks',
      add2carts: 'Add2carts',
    },
    downloadFile: 'RelatedPromptsData',
  },
  conversational: {
    insights: {
      title: 'Insights',
      uniqueQueries: {
        label: 'Unique Queries',
        description:
          'Each unique query reveals a specific customer need and uncovers opportunities you might otherwise miss.',
      },
      naturalConversations: {
        label: 'Natural Conversations',
        description:
          'Queries over 80 characters that show richer, more detailed customer intent beyond what traditional search can capture.',
      },
      topPerformers: {
        label: 'Top 3 Performers',
        stats: {
          time: 'time',
          times: 'times',
          clicks: 'clicks',
          ctr: 'CTR',
        },
      },
    },
    table: {
      title: 'queries',
      columns: {
        query: 'Query',
        quantity: 'Quantity',
        ctr: 'CTR',
        clicks: 'Clicks',
      },
    },
  },
}

const DEFAULT_ORIGINS_MESSAGES_ES = {
  [OriginIdEnum.SEARCH_BOX]: {
    description: 'Cualquier query escrita por el usuario en la caja de búsqueda',
  },
  [OriginIdEnum.POPULAR_SEARCH]: {
    description: 'Query seleccionada desde las búsquedas populares',
  },
  [OriginIdEnum.HISTORY_QUERY]: {
    description: 'Query seleccionada desde el historial',
  },
  [OriginIdEnum.QUERY_SUGGESTION]: {
    description: 'Query seleccionada desde las sugerencias',
  },
  [OriginIdEnum.QUERY_SUGGESTION_WITH_FACET]: {
    description: 'Query filtrada por categoría seleccionada desde las sugerencias',
  },
  [OriginIdEnum.IDENTIFIER_SUGGESTION]: {
    description: 'Query seleccionada desde una sugerencia de ID',
  },
  [OriginIdEnum.PARTIAL_RESULT]: {
    description: 'Query generada desde una sugerencia parcial',
  },
  [OriginIdEnum.SPELLCHECK]: {
    description: 'Query generada por una sugerencia del corrector',
  },
  [OriginIdEnum.RELATED_TAG]: {
    description: 'Query generada desde una related tag',
  },
  [OriginIdEnum.NEXT_QUERY]: {
    description: 'Query generada desde una next query',
  },
  [OriginIdEnum.SEMANTICS]: {
    description: '',
  },
  [OriginIdEnum.URL]: {
    description: '',
  },
}
const DEFAULT_KPIS_MESSAGES_ES = {
  queries: {
    valueLabel: 'queries',
  },
  noResults: {
    valueLabel: 'no results',
  },
  clicks: {
    valueLabel: 'clicks',
    percentageLabel: 'CTR',
  },
  findability: {
    valueLabel: 'success clicks',
    percentageLabel: 'findability',
  },
  add2cart: {
    valueLabel: 'add2carts',
    percentageLabel: 'Add2cart',
  },
  positionAvg: {
    valueLabel: 'avg. position',
  },
  checkout: {
    valueLabel: 'checkouts',
    percentageLabel: 'Checkout',
  },
  sessions: {
    valueLabel: 'sesiones',
  },
  queriesPerSession: {
    valueLabel: 'Q/Sesión',
  },
  conversion: {
    valueLabel: 'Conversion',
  },
}

const INSIGHTS_MESSAGES_ES: InsightsMessages = {
  now: {
    totalQueries: 'Total queries',
    queries: {
      counter: 'Result queries',
      kpi: 'Queries',
    },
    clicks: 'Clicks',
    noResults: 'No results',
  },
  origins: {
    landing: {
      paragraph1:
        'La experiencia de búsqueda proporciona diferentes vías para encontrar lo que estás buscando: ya sea escribiendo ' +
        'una query o escogiendo entre distintas sugerencias.',
      paragraph2: `El origen de la query puede ser de tipo <b>Default</b>, <b>Predictive</b> o <b>Refinement</b>`,
    },
    steps: {
      [OriginsStepEnum.DEFAULT]: {
        label: '',
        description:
          'Queries escritas sin ningún tipo de sugerencia externa: se escribe exactamemente lo que se está buscando.',
      },
      [OriginsStepEnum.PREDICTIVE]: {
        label: 'Predictive',
        description:
          'La búsqueda se realiza a través de las sugerencias ofrecidas, sin escribir ninguna query.',
      },
      [OriginsStepEnum.REFINEMENT]: {
        label: 'Refinement',
        description: 'La búsqueda es la combinación de una query con las sugerencias ofrecidas',
      },
    },
    skipAnimation: 'Skip animation',
    component: {
      origins: {
        ...DEFAULT_ORIGINS_MESSAGES_ES,
        [OriginIdEnum.DEFAULT]: DEFAULT_ORIGINS_MESSAGES_ES[OriginIdEnum.SEARCH_BOX],
        [OriginIdEnum.DEFAULT_SEARCH]: DEFAULT_ORIGINS_MESSAGES_ES[OriginIdEnum.SEARCH_BOX],
        [OriginIdEnum.EMPATHIZE_TRENDING]: DEFAULT_ORIGINS_MESSAGES_ES[OriginIdEnum.POPULAR_SEARCH],
        [OriginIdEnum.TRENDING]: DEFAULT_ORIGINS_MESSAGES_ES[OriginIdEnum.POPULAR_SEARCH],
        [OriginIdEnum.HISTORY]: DEFAULT_ORIGINS_MESSAGES_ES[OriginIdEnum.HISTORY_QUERY],
        [OriginIdEnum.HISTORICAL]: DEFAULT_ORIGINS_MESSAGES_ES[OriginIdEnum.HISTORY_QUERY],
        [OriginIdEnum.EMPATHIZE]: DEFAULT_ORIGINS_MESSAGES_ES[OriginIdEnum.QUERY_SUGGESTION],
        [OriginIdEnum.AUTOCOMPLETE]: DEFAULT_ORIGINS_MESSAGES_ES[OriginIdEnum.QUERY_SUGGESTION],
        [OriginIdEnum.EMPATHIZE_CATEGORY]:
          DEFAULT_ORIGINS_MESSAGES_ES[OriginIdEnum.QUERY_SUGGESTION_WITH_FACET],
        [OriginIdEnum.PARTIALS]: DEFAULT_ORIGINS_MESSAGES_ES[OriginIdEnum.PARTIAL_RESULT],
        [OriginIdEnum.RELATED_TAGS]: DEFAULT_ORIGINS_MESSAGES_ES[OriginIdEnum.RELATED_TAG],
        [OriginIdEnum.RELATED]: DEFAULT_ORIGINS_MESSAGES_ES[OriginIdEnum.RELATED_TAG],
        [OriginIdEnum.EMPATHIZE_NEXT_QUERIES]: DEFAULT_ORIGINS_MESSAGES_ES[OriginIdEnum.NEXT_QUERY],
        [OriginIdEnum.WHATS_NEXT]: DEFAULT_ORIGINS_MESSAGES_ES[OriginIdEnum.NEXT_QUERY],
        [OriginIdEnum.WHATS_NEXT_CAROUSEL]: DEFAULT_ORIGINS_MESSAGES_ES[OriginIdEnum.NEXT_QUERY],
      },
    },
  },
  keywords: {
    main: {
      titles: {
        top: 'Top {count} terms por {mode}',
        compare: 'Comparativa',
      },
      info: '<b>Crecimiento:</b> Términos de búsqueda en auge.<br> <b>Popularidad:</b> Términos de búsqueda principales.',
      modes: {
        [KeywordsModeEnum.GROWTH]: 'Crecimiento',
        [KeywordsModeEnum.POPULARITY]: 'Popularidad',
      },
      noDataFound:
        'No se han encontrado datos para este periodo y filtros. Por favor, pruebe otros valores.',
    },
    aside: {
      title: 'Top queries',
      noQueries: 'No se han encontrado queries para este term',
    },
  },
  categories: {
    findabilityTitle: 'Performance by category',
    rootTitle: 'Todas las categorías',
    asideIdLabel: 'CAT_ID:',
    asideInfo:
      'Algunos porcentajes pueden superar el 100 % ya que se pueden enviar múltiples eventos para una única query.',
    categoryLabel: 'Categoría',
    percentageLabel: 'Porcentaje',
    download: 'Descargar',
    tooltips: {
      queries:
        'Total de peticiones al search en una categoría respecto al total de todas las categorías',
      findability:
        'Cantidad de clicks en la primera página de resultados respecto al total de queries',
      add2cart: 'Cantidad de productos añadidos a la cesta respecto al total de queries',
      volume: 'Cantidad de queries totales en el periodo de tiempo seleccionado',
      categoriesTable: {
        category: 'Nombre de la categoría',
        percentage:
          'Cantidad de queries en porcentaje respecto a la cantidad total de todas las categorías',
        findability:
          'Cantidad de clicks en la primera página de resultados respecto al total de la categoría',
        add2cart: 'Porcentaje de la cantidad de productos añadidos a la cesta para una categoría',
      },
      queriesTable: {
        query: 'Query asociada a la categoría seleccionada',
        percentage:
          'Cantidad de queries en porcentaje respecto a la cantidad total de la categoría',
        findability:
          'Cantidad de clicks en la primera página de resultados respecto al total de queries de una query concreta',
        add2cart: 'Porcentaje de la cantidad de productos añadidos a la cesta para una query',
      },
    },
  },
  analytics: {
    kpis: Object.assign({}, DEFAULT_KPIS_MESSAGES_ES, {}),
    details: {
      overall: 'Mostrando datos globales',
      selectedId: 'Mostrando datos para "{terms}"',
    },
    kpisSuccess: {
      overall: 'Métricas de éxito',
      selectedId: 'Métricas de éxito para "{terms}"',
    },
    evolutionTitle: 'Evolución en el tiempo',
    distributionTitle: 'Distribución de queries',
    previousPeriodLabel: '{kpi} periodo anterior',
    currentPeriodLabel: '{kpi} periodo actual',
    showPercentage: 'Mostrar porcentaje',
    showRadarChart: 'Mostrar gráfico',
    timezone: 'Tiempo UTC',
    volume: 'Volumen',
    noFindability: 'no clicks',
    queryName: 'Query',
    product: 'Nombre',
    productId: 'ID',
    quantity: 'Cantidad',
    queries: {
      queriesTypes: {
        [QueriesTypeEnum.ALL]: 'Todas las búsquedas',
        [QueriesTypeEnum.WITH_RESULTS]: 'Búsquedas con resultados',
        [QueriesTypeEnum.WITHOUT_RESULTS]: 'Búsquedas sin resultados',
      },
      distributionTitle: 'Distribución de queries para "{query}"',
      relatedTitle: 'Productos relacionados para "{query}"',
      search: {
        placeholder: 'Escribe una query',
        resultsSummary: '{count} queries with "{terms}"',
        noResultsFound: 'No results found for "{terms}"',
      },
      getBack: 'Volver atrás',
    },
  },
  toolReporting: {
    noResultsFound: {
      title: '',
      description: {
        paragraph1: '',
        paragraph2: '',
      },
    },
    preDatesSelected: {
      title: '',
      description: {
        paragraph1: '',
        paragraph2: '',
      },
    },
    columns: {
      name: 'Name',
      queries: 'Queries',
      ctr: 'CTR',
    },
    toggle: {
      graph: '',
      table: '',
    },
    stats: {
      ctrAvg: '',
      totalClicks: '',
    },
    aside: {
      warningMessage: '',
      sections: {
        created: {
          title: 'Created',
          creationDate: 'On',
          user: 'By',
        },
        ctr: '',
        schedule: 'Schedule',
        queries: 'Queries',
        promotion: 'Promotion',
        position: 'Position',
        averagePosition: {
          title: 'Average position',
          help: '',
        },
        evolution: 'Evolution over time',
      },
      dailyChart: {
        errorMessage: '',
      },
    },
    ctrPerformance: {
      high: {
        label: 'High',
      },
      medium: {
        label: 'Medium',
      },
      low: {
        label: 'Low',
      },
    },
  },
  semantics: {
    header: {
      totalQueries: '',
      clicksGained: '',
      add2cartsGained: '',
      noResultsDrop: '',
    },
    insightsBar: {
      title: {
        noResults: '',
        interactionsGained: '',
      },
      noResultsBar: {
        main: '',
        secondary: '',
      },
      interactionsGainedBar: {
        secondary: {
          clicks: '',
          add2cart: '',
        },
        tooltip: '',
      },
    },
    semanticsQueriesTitle: '',
    timelineGraph: {
      semanticsQueries: '',
      noResults: '',
      clicks: '',
      add2carts: '',
    },
    table: {
      quantity: '',
      clicks: '',
      add2carts: '',
      checkouts: '',
    },
    downloadFile: '',
  },
  relatedPrompts: {
    summary: '',
    tabs: {
      overall: '',
      noResults: '',
      lowResults: '',
    },
    table: {
      relatedPrompts: '',
      clicks: '',
      add2carts: '',
      promptsClicks: '',
    },
    timelineGraph: {
      clicks: '',
      add2carts: '',
    },
    downloadFile: '',
  },
  conversational: {
    insights: {
      title: '',
      uniqueQueries: {
        label: '',
        description: '',
      },
      naturalConversations: {
        label: '',
        description: '',
      },
      topPerformers: {
        label: '',
        stats: {
          time: '',
          times: '',
          clicks: '',
          ctr: '',
        },
      },
    },
    table: {
      title: '',
      columns: {
        query: '',
        quantity: '',
        ctr: '',
        clicks: '',
      },
    },
  },
}

export const INSIGHTS_MESSAGES: Dictionary<{ insights: InsightsMessages }> = {
  [LocaleEnum.ENGLISH]: {
    insights: INSIGHTS_MESSAGES_EN,
  },
  [LocaleEnum.SPANISH]: {
    insights: INSIGHTS_MESSAGES_ES,
  },
}
